import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  get_staff,
  getDesignations,
  manage_staff,
  upload_image,
} from "../../reduxData/user/userAction";
import DefaultImg from "../../images/no_profile.png";
import { GET_SINGLE_STAFF } from "../../reduxData/user/userTypes";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const { REACT_APP_IMAGE_URL } = process.env;

const EMPTY_PERMISSION = { view: false, create: false, edit: false, delete: false };

const PERMISSIONS = {
  analytics_dashboard: { view: false, create: false, edit: false, delete: false },
  categories: { view: false, create: false, edit: false, delete: false },
  sizes: { view: false, create: false, edit: false, delete: false },
  brand: { view: false, create: false, edit: false, delete: false },
  users: { view: false, create: false, edit: false, delete: false },
  staffs: { view: false, create: false, edit: false, delete: false },
  bulk_upload: { view: false, create: false, edit: false, delete: false },
  products: { view: false, create: false, edit: false, delete: false },
  orders: { view: false, create: false, edit: false, delete: false },
  disputes: { view: false, create: false, edit: false, delete: false },
  // returns: { view: false, create: false, edit: false, delete: false },
  payments: { view: false, create: false, edit: false, delete: false },
  logs_tracking: { view: false, create: false, edit: false, delete: false },
  newsletter: { view: false, create: false, edit: false, delete: false },
  queries: { view: false, create: false, edit: false, delete: false },
  cms: { view: false, create: false, edit: false, delete: false },
  notifications: { view: false, create: true, edit: true, delete: true },
  profile: { view: true, create: false, edit: true, delete: false },
  change_password: { view: true, create: false, edit: true, delete: false },
};

const MODULE_ACTIONS = {
  analytics_dashboard: ["view"],
  categories: ["view", "create", "edit", "delete"],
  sizes: ["view", "create", "edit", "delete"],
  brand: ["view", "create", "edit", "delete"],
  users: ["view", "edit", "delete"],
  bulk_upload: ["view", "edit"],
  staffs: ["view", "create", "edit"],
  products: ["view", "edit"],
  orders: ["view"],
  disputes: ["view", "edit"],
  // returns: ["view"],
  payments: ["view"],
  logs_tracking: ["view"],
  newsletter: ["view", "delete"],
  queries: ["view", "edit"],
  cms: ["view", "edit", "create", "delete"],
  notifications: ["view"],
  profile: ["view", "edit"],
  change_password: ["edit"],
};

const DEFAULT_TRUE_ACTIONS = {
  // analytics_dashboard: ["view"],
  profile: ["view", "edit"],
  change_password: ["view", "edit"],
};

const PERMISSION_MODULES = Object.keys(PERMISSIONS);
const ACTIONS = ["view", "create", "edit", "delete"];

const enforcePermissionRules = (permissionState) => {
  const next = PERMISSION_MODULES.reduce((acc, module) => {
    const savedModule = permissionState?.[module] || {};
    acc[module] = { ...EMPTY_PERMISSION, ...savedModule };
    return acc;
  }, {});

  Object.entries(DEFAULT_TRUE_ACTIONS).forEach(([module, actions]) => {
    actions.forEach((action) => {
      next[module][action] = true;
    });
  });

  PERMISSION_MODULES.forEach((module) => {
    if (next[module].create || next[module].edit || next[module].delete) {
      next[module].view = true;
    }
  });

  if (next.sizes.create || next.sizes.edit) {
    next.categories.view = true;
  }

  if (next.bulk_upload.view) {
    next.products.view = true;
  }

  if (next.bulk_upload.edit) {
    next.products.edit = true;
    next.products.view = true;
  }

  if (next.queries.edit) {
    next.staffs.view = true;
  }

  return next;
};

const normalizePermissions = (permissionState) => enforcePermissionRules({
  ...PERMISSIONS,
  ...(permissionState || {}),
});

const AddEditStaff = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { singleStaff } = useSelector((state) => state.user);
  const { userPermissions } = useSelector((state) => state.auth);

  const DESIGNATION = useMemo(() => getDesignations(), [i18n.language]);

  const [permissions, setPermissions] = useState(PERMISSIONS);
  const [profilePic, setProfilePic] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const imageRef = useRef();

  const STATUS_OPTIONS = [
    { label: t("addEditStaff.statusActive"), value: false },
    { label: t("addEditStaff.statusInactive"), value: true },
  ];

  // Action label map for permissions
  const ACTION_LABEL_MAP = {
    view: t("addEditStaff.actionView"),
    create: t("addEditStaff.actionCreate"),
    edit: t("addEditStaff.actionEdit"),
    delete: t("addEditStaff.actionDelete"),
  };

  const PERMISSION_ACTION_LABEL_MAP = {
    "categories.delete": t("addEditStaff.permissionActions.activeInactive"),
    "brand.delete": t("addEditStaff.permissionActions.activeInactive"),
    "users.edit": t("addEditStaff.permissionActions.sellerApproveReject"),
    "users.delete": t("addEditStaff.permissionActions.suspendUser"),
    "bulk_upload.edit": t("addEditStaff.permissionActions.productApprove"),
    "products.edit": t("addEditStaff.permissionActions.approveReject"),
    "cms.create": t("addEditStaff.permissionActions.active"),
    "cms.delete": t("addEditStaff.permissionActions.inactive"),
  };

  const validationSchema = yup.object({
    name: yup.string().required("addEditStaff.nameRequired"),
    email: yup.string().email("addEditStaff.emailInvalid").required("addEditStaff.emailRequired"),
    designation: yup.string().required("addEditStaff.designationRequired"),
    password: yup.string().when([], {
      is: () => !id,
      then: (s) => s.required("addEditStaff.passwordRequired").min(6, "addEditStaff.passwordMinLength"),
      otherwise: (s) => s.nullable().notRequired().min(6, "addEditStaff.passwordMinLength"),
    }),
    isDeactive: yup.mixed(),
  });

  const { control, handleSubmit, setValue, setError, clearErrors, reset, formState: { errors } } = useForm({
    defaultValues: { name: null, email: null, designation: null, isDeactive: false, password: null },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (id) get_staff(dispatch, id);
    return () => dispatch({ type: GET_SINGLE_STAFF, payload: null });
  }, [id, dispatch]);

  useEffect(() => {
    if (id && singleStaff) {
      reset({
        name: singleStaff?.user_name,
        email: singleStaff?.email,
        designation: singleStaff?.role,
        isDeactive: singleStaff?.is_deactive,
      });
      setPermissions(normalizePermissions(singleStaff?.permissions));
      setImgPreview(singleStaff?.profile_pic);
    }
  }, [id, singleStaff, reset]);

  const togglePermission = (module, action, prevVal) => {
    if (DEFAULT_TRUE_ACTIONS[module]?.includes(action) && prevVal === true) return;
    setPermissions((prev) => normalizePermissions({
      ...prev,
      [module]: { ...prev[module], [action]: !prev[module][action] },
    }));
  };

  const handleImage = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (!file) return;
    const maxSize = 40 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(t("addEditStaff.fileSizeError"), { toastId: "staffProfileAuth", autoClose: 2000 });
      return;
    }
    setProfilePic(file);
    setImgPreview(URL.createObjectURL(file));
  };

  const getPreviewSrc = () => {
    if (!imgPreview) return DefaultImg;
    if (
      imgPreview.startsWith("http://") ||
      imgPreview.startsWith("https://") ||
      imgPreview.startsWith("blob:") ||
      imgPreview.startsWith("data:")
    ) return imgPreview;
    return `${REACT_APP_IMAGE_URL}${imgPreview}`;
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      if (Object.keys(errors).length > 0) return;

      let profile = imgPreview;
      if (profile && profilePic) {
        const res = await upload_image(profilePic, dispatch);
        if (res?.data?.status) {
          profile = res.data.data;
          setImgPreview(res.data.data);
        }
      }

      const payload = {
        email: data.email,
        role: data.designation,
        is_deactive: data.isDeactive,
        permissions: normalizePermissions(permissions),
      };

      if (profile) payload.profile_pic = profile;
      if (id) payload.user_name = data.name;
      if (!id) {
        payload.name = data.name;
        payload.password = data.password;
      }

      const res = await manage_staff(payload, id, dispatch);
      if (res) navigate("/staff");
    } catch (err) {
      console.error("Submit Error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    (userPermissions?.staffs?.edit && id) ||
    (userPermissions?.staffs?.create && !id);

  return (
    <div className="aes-wrapper">

      {/* ── Header ── */}
      <div className="aes-header">
        <div className="aes-header-icon">
          <i className={`fas fa-${id ? "user-edit" : "user-plus"}`} />
        </div>
        <div>
          <h2>{id ? t("addEditStaff.editTitle") : t("addEditStaff.addTitle")}</h2>
          <p>{id ? t("addEditStaff.editSubtitle") : t("addEditStaff.addSubtitle")}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="aes-card">

          {/* ── Avatar Section ── */}
          <div className="aes-section">
            <div className="aes-section-title">
              <div className="aes-section-title-dot" />
              <h4>{t("addEditStaff.profilePhotoSection")}</h4>
            </div>

            <div className="aes-avatar-block">
              <div className="aes-avatar-wrap">
                <img src={getPreviewSrc()} alt={t("header.profile")} className="aes-avatar" />
                <div className="aes-avatar-badge">
                  <i className="fas fa-camera" />
                </div>
              </div>
              <div className="aes-avatar-info">
                <h5>{id ? t("addEditStaff.updatePhoto") : t("addEditStaff.uploadPhoto")}</h5>
                <p>{t("addEditStaff.photoHint")}</p>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                  className="d-none"
                  ref={imageRef}
                  onChange={handleImage}
                />
                <button type="button" className="aes-upload-btn" onClick={() => imageRef.current.click()}>
                  <i className="fas fa-upload" />
                  {id ? t("addEditStaff.changeImage") : t("addEditStaff.uploadImage")}
                </button>
              </div>
            </div>
          </div>

          {/* ── Basic Info ── */}
          <div className="aes-section">
            <div className="aes-section-title">
              <div className="aes-section-title-dot" />
              <h4>{t("addEditStaff.basicInfoSection")}</h4>
            </div>

            <div className="aes-grid">

              {/* Name */}
              <div className="aes-field">
                <label className="aes-label">{t("addEditStaff.nameLabel")} <span>*</span></label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <div className="aes-input-wrap">
                      <i className="fas fa-user aes-input-icon" />
                      <input
                        className={`aes-input${errors.name ? " error" : ""}`}
                        placeholder={t("addEditStaff.namePlaceholder")}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </div>
                  )}
                />
                {errors.name && <span className="aes-error-text"><i className="fas fa-exclamation-circle" />{t(errors.name.message)}</span>}
              </div>

              {/* Email */}
              <div className="aes-field">
                <label className="aes-label">{t("addEditStaff.emailLabel")} <span>*</span></label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <div className="aes-input-wrap">
                      <i className="fas fa-envelope aes-input-icon" />
                      <input
                        className={`aes-input${errors.email ? " error" : ""}`}
                        placeholder={t("addEditStaff.emailPlaceholder")}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </div>
                  )}
                />
                {errors.email && <span className="aes-error-text"><i className="fas fa-exclamation-circle" />{t(errors.email.message)}</span>}
              </div>

              {/* Designation */}
              <div className="aes-field">
                <label className="aes-label">{t("addEditStaff.designationLabel")} <span>*</span></label>
                <Controller
                  name="designation"
                  control={control}
                  render={({ field }) => (
                    <div className="aes-input-wrap">
                      <i className="fas fa-briefcase aes-input-icon" />
                      <select
                        className={`aes-select${errors.designation ? " error" : ""}`}
                        {...field}
                        value={field.value ?? ""}
                      >
                        <option value="" disabled>{t("addEditStaff.designationPlaceholder")}</option>
                        {DESIGNATION.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <i className="fas fa-chevron-down aes-select-arrow" />
                    </div>
                  )}
                />
                {errors.designation && <span className="aes-error-text"><i className="fas fa-exclamation-circle" />{t(errors.designation.message)}</span>}
              </div>

              {/* Status */}
              <div className="aes-field">
                <label className="aes-label">{t("addEditStaff.statusLabel")}</label>
                <Controller
                  name="isDeactive"
                  control={control}
                  render={({ field }) => (
                    <div className="aes-input-wrap">
                      <i className="fas fa-toggle-on aes-input-icon" />
                      <select
                        className="aes-select"
                        {...field}
                        value={field.value === true ? "true" : field.value === false ? "false" : ""}
                        onChange={(e) => field.onChange(e.target.value === "true")}
                      >
                        <option value="" disabled>{t("addEditStaff.selectStatus")}</option>
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
                        ))}
                      </select>
                      <i className="fas fa-chevron-down aes-select-arrow" />
                    </div>
                  )}
                />
              </div>

              {/* Password */}
              {!id && (
                <div className="aes-field">
                  <label className="aes-label">
                    {t("addEditStaff.passwordLabel")} <span>*</span>
                  </label>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <div className="aes-input-wrap">
                        <i className="fas fa-lock aes-input-icon" />
                        <input
                          type={showPassword ? "text" : "password"}
                          className={`aes-input${errors.password ? " error" : ""}`}
                          placeholder={t("addEditStaff.passwordPlaceholder")}
                          autoComplete="new-password"
                          {...field}
                          value={field.value ?? ""}
                        />
                        <button
                          type="button"
                          className="aes-pass-toggle"
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          <i className={`fas fa-${showPassword ? "eye-slash" : "eye"}`} />
                        </button>
                      </div>
                    )}
                  />
                  {errors.password && <span className="aes-error-text"><i className="fas fa-exclamation-circle" />{t(errors.password.message)}</span>}
                </div>
              )}

            </div>
          </div>

          {/* ── Permissions ── */}
          <div className="aes-section">
            <div className="aes-section-title">
              <div className="aes-section-title-dot" />
              <h4>{t("addEditStaff.permissionsSection")}</h4>
              <span>{t("addEditStaff.permissionsSubtitle")}</span>
            </div>

            <div className="aes-perm-grid">
              {PERMISSION_MODULES.map((module) => (
                <div key={module} className="aes-perm-card">
                  <div className="aes-perm-module">
                    <span className="aes-perm-module-icon" />
                    {/* {module.replaceAll("_", " ")} */}
                    {t(`addEditStaff.modules.${module}`)}
                  </div>
                  <div className="aes-perm-actions">
                    {ACTIONS.filter((action) => MODULE_ACTIONS[module]?.includes(action)).map((action) => {
                      const isDefault = DEFAULT_TRUE_ACTIONS[module]?.includes(action);
                      const actionLabel = PERMISSION_ACTION_LABEL_MAP[`${module}.${action}`] || ACTION_LABEL_MAP[action] || action;
                      return (
                        <label key={action} className="aes-perm-check">
                          <input
                            type="checkbox"
                            checked={permissions?.[module]?.[action] || false}
                            disabled={isDefault}
                            onChange={() => togglePermission(module, action, permissions?.[module]?.[action])}
                          />
                          <span className="aes-perm-label">
                            {actionLabel}
                            {/* {action} */}
                          </span>
                          {isDefault && <span className="aes-perm-default-badge">{t("addEditStaff.permissionDefault")}</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Footer ── */}
          {canSubmit && (
            <div className="aes-footer">
              <button type="submit" className="aes-btn-submit" disabled={isSubmitting}>
                {isSubmitting
                  ? <><i className="fas fa-spinner fa-spin" /> {t("addEditStaff.submitSaving")}</>
                  : <><i className="fas fa-check" /> {id ? t("addEditStaff.submitSaveChanges") : t("addEditStaff.submitCreateStaff")}</>
                }
              </button>
              <button type="button" className="aes-btn-cancel" onClick={() => navigate("/staff")}>
                <i className="fas fa-times" /> {t("addEditStaff.cancelButton")}
              </button>
            </div>
          )}

        </div>
      </form>
    </div>
  );
};

export default AddEditStaff;
